import {
  Component, Input, Output, EventEmitter,
  OnChanges, OnDestroy, SimpleChanges,
  ChangeDetectorRef, inject, ElementRef, ViewChild
} from '@angular/core';
import { AgentStepEnhanced, Blog } from '../../types';

interface StepTypeConfig {
  icon: string;
  color: string;
}

const STEP_TYPE_MAP: Record<string, StepTypeConfig> = {
  Prompt:     { icon: 'fa-brain',        color: 'var(--mj-color-violet-400)' },
  Actions:    { icon: 'fa-gear',         color: 'var(--mj-color-indigo-500)' },
  'Sub-Agent':{ icon: 'fa-robot',        color: 'var(--mj-color-info-500)' },
  Chat:       { icon: 'fa-comment',      color: 'var(--mj-text-disabled)' },
  Decision:   { icon: 'fa-code-branch',  color: 'var(--mj-status-warning)' },
  ForEach:    { icon: 'fa-repeat',       color: 'var(--mj-color-indigo-500)' },
  While:      { icon: 'fa-rotate',       color: 'var(--mj-color-indigo-500)' },
  Validation: { icon: 'fa-check-double', color: 'var(--mj-status-success)' },
};

@Component({
  standalone: false,
  selector: 'mkt-agent-sidebar',
  template: `
    <!-- ── Collapsed strip ────────────────────────── -->
    <div class="sidebar-collapsed" *ngIf="collapsed" (click)="expand()">
      <div class="collapsed-icon-wrap">
        <i class="fa-solid fa-brain"></i>
        <span class="pulse-dot" *ngIf="isRunning"></span>
      </div>
      <span class="step-count-badge" *ngIf="steps.length">{{ steps.length }}</span>
      <i class="fa-solid fa-chevron-left collapsed-expand"></i>
    </div>

    <!-- ── Expanded panel ─────────────────────────── -->
    <div class="sidebar-expanded" *ngIf="!collapsed">

      <!-- Header -->
      <div class="sidebar-header">
        <div class="header-left">
          <i class="fa-solid fa-brain header-icon"></i>
          <span class="header-title">CMO Agent</span>
          <span class="pulse-dot" *ngIf="isRunning"></span>
        </div>
        <button class="collapse-btn" (click)="collapse()" title="Collapse sidebar">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <!-- Instructions -->
      <div class="instructions-section">
        <textarea
          class="instructions-input"
          [(ngModel)]="instructions"
          placeholder="Tell the CMO what to do..."
          rows="3"
          [disabled]="isRunning"
        ></textarea>
        <div class="instructions-row">
          <button
            class="run-btn"
            [disabled]="isRunning"
            (click)="onRun()"
          >
            <i class="fa-solid fa-play" style="margin-right:4px"></i>
            Run CMO
          </button>
          <span class="elapsed" *ngIf="elapsed">{{ elapsed }}</span>
        </div>
        <a
          class="explorer-link"
          *ngIf="currentRunId && !isRunning"
          [href]="explorerLinkBase + currentRunId"
          target="_blank"
        >
          View in Explorer <i class="fa-solid fa-arrow-up-right-from-square"></i>
        </a>
      </div>

      <!-- Steps feed -->
      <div
        class="steps-container"
        #stepsContainer
        (scroll)="onStepsScroll()"
      >
        <ng-container *ngFor="let step of steps; trackBy: trackStep">
          <ng-container *ngTemplateOutlet="stepRow; context: { $implicit: step, depth: 0 }"></ng-container>
        </ng-container>

        <!-- Blinking cursor while running -->
        <div class="running-cursor" *ngIf="isRunning">_</div>
      </div>

      <!-- Run output -->
      <div class="run-output" *ngIf="runOutputBlogs.length > 0 && !isRunning">
        <div class="output-header">
          Output &mdash; {{ runOutputBlogs.length }} blog{{ runOutputBlogs.length === 1 ? '' : 's' }} created
        </div>
        <div class="output-item" *ngFor="let blog of runOutputBlogs">
          <span class="output-title">{{ blog.Title || '(untitled)' }}</span>
          <span class="output-status" [ngClass]="'status-' + (blog.Status || 'Draft').toLowerCase()">
            {{ blog.Status || 'Draft' }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Reusable step row template ────────────── -->
    <ng-template #stepRow let-step let-depth="depth">
      <div
        class="step-row"
        [class.step-error]="step.status === 'Failed'"
        [class.step-nested]="depth > 0"
        [style.margin-left.px]="depth * 16"
        [style.border-left]="depth > 0 ? '2px solid var(--mj-border-default)' : 'none'"
        [style.padding-left.px]="depth > 0 ? 8 : 0"
      >
        <!-- Main row -->
        <div class="step-main" (click)="toggleStep(step)">
          <i
            class="fa-solid step-icon"
            [ngClass]="getStepIcon(step.stepType)"
            [style.color]="getStepColor(step.stepType)"
          ></i>
          <span class="step-name">{{ step.stepName || step.stepType }}</span>
          <span class="step-duration" *ngIf="step.duration">{{ step.duration }}</span>
          <span class="step-status-dot" [ngClass]="'dot-' + step.status.toLowerCase()"></span>
          <i
            class="fa-solid fa-chevron-down step-expand-icon"
            *ngIf="hasExpandableData(step)"
            [class.rotated]="step.expanded"
          ></i>
        </div>

        <!-- Summary line -->
        <div class="step-summary" *ngIf="!step.expanded">
          {{ getStepSummary(step) }}
        </div>

        <!-- Error message -->
        <div class="step-error-msg" *ngIf="step.errorMessage && !step.expanded">
          {{ step.errorMessage }}
        </div>

        <!-- Expanded detail -->
        <div class="step-detail" *ngIf="step.expanded">
          <div class="step-error-msg" *ngIf="step.errorMessage">
            {{ step.errorMessage }}
          </div>
          <div class="detail-block" *ngIf="step.inputData">
            <div class="detail-label">Input</div>
            <pre class="detail-code"
              [class.truncated]="step.inputData.length > 500 && !isFullyExpanded(step, 'input')"
            >{{ step.inputData.length > 500 && !isFullyExpanded(step, 'input')
                ? step.inputData.slice(0, 500) + '...'
                : formatJson(step.inputData) }}</pre>
            <button
              class="show-more-btn"
              *ngIf="step.inputData.length > 500"
              (click)="toggleFullExpand(step, 'input', $event)"
            >
              {{ isFullyExpanded(step, 'input') ? 'Show less' : 'Show more' }}
            </button>
          </div>
          <div class="detail-block" *ngIf="step.outputData">
            <div class="detail-label">Output</div>
            <pre class="detail-code"
              [class.truncated]="step.outputData.length > 500 && !isFullyExpanded(step, 'output')"
            >{{ step.outputData.length > 500 && !isFullyExpanded(step, 'output')
                ? step.outputData.slice(0, 500) + '...'
                : formatJson(step.outputData) }}</pre>
            <button
              class="show-more-btn"
              *ngIf="step.outputData.length > 500"
              (click)="toggleFullExpand(step, 'output', $event)"
            >
              {{ isFullyExpanded(step, 'output') ? 'Show less' : 'Show more' }}
            </button>
          </div>
        </div>

        <!-- Child steps for Sub-Agent -->
        <div class="child-steps" *ngIf="step.stepType === 'Sub-Agent' && step.childSteps?.length && step.expanded">
          <ng-container *ngFor="let child of step.childSteps; trackBy: trackStep">
            <ng-container *ngTemplateOutlet="stepRow; context: { $implicit: child, depth: depth + 1 }"></ng-container>
          </ng-container>
        </div>
      </div>
    </ng-template>
  `,
  styles: [`
    /* ─── Host & layout ───────────────────────────── */
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      border-left: 1px solid var(--mj-border-default);
      background: var(--mj-bg-surface);
      transition: width 0.2s ease;
      overflow: hidden;
    }

    /* ─── Collapsed strip ─────────────────────────── */
    .sidebar-collapsed {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 12px 0;
      width: 48px;
      height: 100%;
      cursor: pointer;
    }
    .sidebar-collapsed:hover {
      background: var(--mj-bg-surface-hover);
    }
    .collapsed-icon-wrap {
      position: relative;
      font-size: 1.1rem;
      color: var(--mj-text-secondary);
    }
    .step-count-badge {
      background: var(--mj-color-indigo-500);
      color: var(--mj-text-inverse);
      font-size: 0.65rem;
      font-weight: 600;
      border-radius: 999px;
      min-width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
    }
    .collapsed-expand {
      color: var(--mj-text-muted);
      font-size: 0.7rem;
      margin-top: auto;
      margin-bottom: 8px;
    }

    /* ─── Pulse dot (running indicator) ───────────── */
    .pulse-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--mj-status-success);
      animation: pulse 1.4s ease-in-out infinite;
    }
    .collapsed-icon-wrap .pulse-dot {
      position: absolute;
      top: -2px;
      right: -4px;
      width: 7px;
      height: 7px;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.85); }
    }

    /* ─── Expanded panel ──────────────────────────── */
    .sidebar-expanded {
      display: flex;
      flex-direction: column;
      width: 320px;
      height: 100%;
      overflow: hidden;
    }

    /* ─── Header ──────────────────────────────────── */
    .sidebar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border-bottom: 1px solid var(--mj-border-default);
      background: var(--mj-bg-surface);
      flex-shrink: 0;
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .header-icon {
      color: var(--mj-color-indigo-500);
      font-size: 1rem;
    }
    .header-title {
      font-weight: 600;
      font-size: 0.85rem;
      color: var(--mj-text-primary);
    }
    .collapse-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      padding: 4px 6px;
      border-radius: 4px;
      font-size: 0.8rem;
    }
    .collapse-btn:hover {
      background: var(--mj-bg-surface-hover);
      color: var(--mj-text-secondary);
    }

    /* ─── Instructions ────────────────────────────── */
    .instructions-section {
      padding: 10px 12px;
      border-bottom: 1px solid var(--mj-border-default);
      background: var(--mj-bg-surface);
      flex-shrink: 0;
    }
    .instructions-input {
      width: 100%;
      box-sizing: border-box;
      resize: none;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      padding: 8px 10px;
      font-size: 0.8rem;
      font-family: inherit;
      line-height: 1.5;
      color: var(--mj-text-primary);
      background: var(--mj-bg-surface);
      outline: none;
      transition: border-color 0.15s;
    }
    .instructions-input:focus {
      border-color: var(--mj-color-indigo-500);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-color-indigo-500) 12%, transparent);
    }
    .instructions-input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .instructions-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 8px;
    }
    .run-btn {
      display: inline-flex;
      align-items: center;
      padding: 6px 14px;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--mj-text-inverse);
      background: var(--mj-color-indigo-500);
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s;
    }
    .run-btn:hover:not(:disabled) {
      background: var(--mj-color-indigo-600, #4f46e5);
    }
    .run-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .elapsed {
      font-size: 0.75rem;
      font-family: 'SF Mono', 'Fira Code', 'Menlo', monospace;
      color: var(--mj-text-muted);
    }
    .explorer-link {
      display: inline-block;
      margin-top: 6px;
      font-size: 0.72rem;
      color: var(--mj-color-indigo-500);
      text-decoration: none;
    }
    .explorer-link:hover {
      text-decoration: underline;
    }

    /* ─── Steps container ─────────────────────────── */
    .steps-container {
      flex: 1;
      overflow-y: auto;
      background: var(--mj-bg-surface-sunken);
      font-family: 'SF Mono', 'Fira Code', 'Menlo', monospace;
      font-size: 0.78rem;
      line-height: 1.7;
      padding: 8px 10px;
    }
    .steps-container::-webkit-scrollbar {
      width: 6px;
    }
    .steps-container::-webkit-scrollbar-thumb {
      background: var(--mj-border-strong);
      border-radius: 3px;
    }
    .steps-container::-webkit-scrollbar-track {
      background: transparent;
    }

    /* ─── Step row ────────────────────────────────── */
    .step-row {
      margin-bottom: 2px;
      border-radius: 4px;
      transition: background 0.1s;
    }
    .step-row:hover {
      background: var(--mj-bg-surface-hover);
    }
    .step-row.step-error {
      background: var(--mj-status-error-bg);
    }
    .step-main {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 3px 4px;
      cursor: pointer;
      user-select: none;
    }
    .step-icon {
      font-size: 0.72rem;
      flex-shrink: 0;
      width: 14px;
      text-align: center;
    }
    .step-name {
      color: var(--mj-text-primary);
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 0.76rem;
    }
    .step-duration {
      color: var(--mj-text-muted);
      font-size: 0.68rem;
      flex-shrink: 0;
    }

    /* ─── Status dots ─────────────────────────────── */
    .step-status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .dot-completed { background: var(--mj-status-success); }
    .dot-running {
      background: var(--mj-status-warning);
      animation: pulse 1.4s ease-in-out infinite;
    }
    .dot-failed { background: var(--mj-status-error); }
    .dot-cancelled { background: var(--mj-text-muted); }

    /* ─── Expand chevron ──────────────────────────── */
    .step-expand-icon {
      font-size: 0.6rem;
      color: var(--mj-text-secondary);
      flex-shrink: 0;
      transition: transform 0.15s, opacity 0.15s;
      opacity: 0;
    }
    .step-main:hover .step-expand-icon {
      opacity: 1;
    }
    .step-expand-icon.rotated {
      transform: rotate(180deg);
      opacity: 1;
    }

    /* ─── Step summary ────────────────────────────── */
    .step-summary {
      color: var(--mj-text-muted);
      font-size: 0.7rem;
      padding: 0 4px 2px 24px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* ─── Error message ───────────────────────────── */
    .step-error-msg {
      color: var(--mj-status-error);
      font-size: 0.7rem;
      padding: 2px 4px 2px 24px;
      word-break: break-word;
    }

    /* ─── Expanded detail ─────────────────────────── */
    .step-detail {
      padding: 4px 4px 6px 24px;
    }
    .detail-block {
      margin-bottom: 6px;
    }
    .detail-label {
      color: var(--mj-color-indigo-500);
      font-size: 0.66rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 2px;
    }
    .detail-code {
      background: var(--mj-bg-surface-sunken);
      border: 1px solid var(--mj-border-strong);
      border-radius: 4px;
      padding: 6px 8px;
      color: var(--mj-color-violet-400, #c4b5fd);
      font-size: 0.7rem;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-all;
      max-height: 300px;
      overflow-y: auto;
      margin: 0;
    }
    .detail-code.truncated {
      max-height: 120px;
      overflow: hidden;
    }
    .show-more-btn {
      background: none;
      border: none;
      color: var(--mj-color-indigo-500);
      font-size: 0.68rem;
      cursor: pointer;
      padding: 2px 0;
      font-family: inherit;
    }
    .show-more-btn:hover {
      text-decoration: underline;
    }

    /* ─── Child steps ─────────────────────────────── */
    .child-steps {
      margin-top: 2px;
    }

    /* ─── Running cursor ──────────────────────────── */
    .running-cursor {
      color: var(--mj-status-success);
      font-weight: bold;
      animation: blink 0.8s step-end infinite;
      padding: 2px 4px;
    }
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }

    /* ─── Run output ──────────────────────────────── */
    .run-output {
      flex-shrink: 0;
      border-top: 1px solid var(--mj-border-default);
      padding: 10px 12px;
      background: var(--mj-bg-surface);
      max-height: 160px;
      overflow-y: auto;
    }
    .output-header {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--mj-text-secondary);
      margin-bottom: 6px;
    }
    .output-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 3px 0;
      font-size: 0.74rem;
    }
    .output-title {
      color: var(--mj-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      margin-right: 8px;
    }
    .output-status {
      font-size: 0.65rem;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 999px;
      flex-shrink: 0;
      text-transform: capitalize;
    }
    .status-draft      { background: var(--mj-bg-surface-hover); color: var(--mj-text-muted); }
    .status-review     { background: var(--mj-status-warning-bg); color: var(--mj-status-warning-text); }
    .status-approved   { background: var(--mj-status-success-bg); color: var(--mj-status-success-text); }
    .status-published  { background: var(--mj-status-info-bg); color: var(--mj-status-info-text); }
    .status-rejected   { background: var(--mj-status-error-bg); color: var(--mj-status-error-text); }
  `]
})
export class AgentSidebarComponent implements OnChanges, OnDestroy {
  // ─── Inputs ───────────────────────────────────────
  @Input() isRunning = false;
  @Input() steps: AgentStepEnhanced[] = [];
  @Input() elapsed = '';
  @Input() collapsed = false;
  @Input() runOutputBlogs: Blog[] = [];
  @Input() currentRunId: string | null = null;
  @Input() explorerLinkBase = '/entity/MJ%3A%20AI%20Agent%20Runs/';

  // ─── Outputs ──────────────────────────────────────
  @Output() runRequested = new EventEmitter<string>();
  @Output() collapsedChange = new EventEmitter<boolean>();

  // ─── Internal state ───────────────────────────────
  instructions = '';
  @ViewChild('stepsContainer') private stepsContainerRef!: ElementRef<HTMLDivElement>;
  private userScrolled = false;
  private fullyExpandedMap = new Map<string, Set<string>>();

  private cdr = inject(ChangeDetectorRef);

  // ─── Lifecycle ────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['steps']) {
      // Auto-scroll to bottom when new steps arrive, unless user scrolled up
      if (!this.userScrolled) {
        this.scrollToBottom();
      }
    }
  }

  ngOnDestroy(): void {
    this.fullyExpandedMap.clear();
  }

  // ─── Collapse / expand ────────────────────────────

  expand(): void {
    this.collapsed = false;
    this.collapsedChange.emit(false);
  }

  collapse(): void {
    this.collapsed = true;
    this.collapsedChange.emit(true);
  }

  // ─── Run ──────────────────────────────────────────

  onRun(): void {
    if (this.isRunning) return;
    this.runRequested.emit(this.instructions);
  }

  // ─── Step helpers ─────────────────────────────────

  getStepIcon(stepType: string): string {
    return STEP_TYPE_MAP[stepType]?.icon ?? 'fa-circle';
  }

  getStepColor(stepType: string): string {
    return STEP_TYPE_MAP[stepType]?.color ?? 'var(--mj-text-disabled)';
  }

  toggleStep(step: AgentStepEnhanced): void {
    if (this.hasExpandableData(step)) {
      step.expanded = !step.expanded;
      this.cdr.detectChanges();
    }
  }

  hasExpandableData(step: AgentStepEnhanced): boolean {
    return !!(step.inputData || step.outputData || step.errorMessage ||
              (step.stepType === 'Sub-Agent' && step.childSteps?.length > 0));
  }

  getStepSummary(step: AgentStepEnhanced): string {
    if (step.errorMessage) {
      return step.errorMessage.length > 80
        ? step.errorMessage.slice(0, 80) + '...'
        : step.errorMessage;
    }
    if (step.outputData) {
      const text = step.outputData.replace(/[\n\r]+/g, ' ').trim();
      return text.length > 80 ? text.slice(0, 80) + '...' : text;
    }
    return '';
  }

  trackStep(_index: number, step: AgentStepEnhanced): string {
    return step.id;
  }

  // ─── JSON formatting ──────────────────────────────

  formatJson(raw: string): string {
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  }

  // ─── Full expand toggle for long data ─────────────

  isFullyExpanded(step: AgentStepEnhanced, section: string): boolean {
    return this.fullyExpandedMap.get(step.id)?.has(section) ?? false;
  }

  toggleFullExpand(step: AgentStepEnhanced, section: string, event: Event): void {
    event.stopPropagation();
    if (!this.fullyExpandedMap.has(step.id)) {
      this.fullyExpandedMap.set(step.id, new Set());
    }
    const sections = this.fullyExpandedMap.get(step.id)!;
    if (sections.has(section)) {
      sections.delete(section);
    } else {
      sections.add(section);
    }
    this.cdr.detectChanges();
  }

  // ─── Auto-scroll ──────────────────────────────────

  onStepsScroll(): void {
    const el = this.stepsContainerRef?.nativeElement;
    if (!el) return;
    // User has scrolled up if not within 40px of bottom
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    this.userScrolled = !atBottom;
  }

  private scrollToBottom(): void {
    // Defer to next tick so DOM has rendered new steps
    Promise.resolve().then(() => {
      const el = this.stepsContainerRef?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
